import React, { Fragment, ReactElement, cloneElement, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { debounce, get } from 'lodash';

import { AutoCompleteProps, FormElementChangeEvent, SelectProps } from '..';
import { FormComponentType, IElementDataSource, IMemo } from '../../../types/form';
import { convertMasterTableDataToPersona, hasOptions as getHasOptions } from '../../../utils/form';
import { IMasterTableData } from '../../../types/models/master-table';
import { IPersona } from '../../../types/models/persona';
import { getByUrl } from '../../../apis/public/master-tables';
import { getLookupFromStateByName } from '../../../state/selectors/lookup';
import useMountedState from '../../../hooks/use-mounted-state';
import { LookupType } from '../../../types/models/lookup';
import { constructDataSourceUrl } from '../../../utils/form-builder';

export type SingleItem = Record<string, any>;
export type ArrayItem = SingleItem[];

export type WithDataSourceProp = {
  dataSource: IElementDataSource;
  children?: ReactElement<any>;
  type: FormComponentType;
  name?: string;
  value?: any;
  onChange?: (event: FormElementChangeEvent, memo?: IMemo) => void;
};

const INITIAL_DATA: ArrayItem = [];
const MIN_SEARCH_CHARACTERS = 3;
const DEBOUNCE_DELAY_IN_MS = 500;

const defaultTitleTransformer = (title: string) => title;

const WithDataSource = memo(function WithDataSource({
  type,
  value,
  dataSource,
  children,
  ...rest
}: WithDataSourceProp) {
  const { lookup, url, titleName, valueName, transformTitle = defaultTitleTransformer } = dataSource;
  const isMounted = useMountedState();
  const isAutoCompleMounted = useRef(false);
  const [state, setState] = useState<{ data: IMasterTableData[]; isLoading: boolean }>({
    data: [],
    isLoading: false,
  });
  const [fullUrl, setFullUrl] = useState(url);
  const hasOptions = getHasOptions(type);
  const isAutoComplete = type === FormComponentType.AutoComplete;
  const isMultiSelectPhotos = type === FormComponentType.MultiSelectPhotos;
  const memoryData = useSelector(getLookupFromStateByName(lookup));

  useEffect(() => {
    setFullUrl(constructDataSourceUrl(url));
  }, [url]);

  const setStateWhenMounted = useCallback(
    (newState) => {
      if (isMounted()) {
        setState((prevState) => ({ ...prevState, ...newState }));
      }
    },
    [isMounted]
  );

  const getSortedClassLevels = (data: any[]) => {
    return [data[data.length - 1], data[data.length - 2]].concat(data.filter((_item, i) => i < data.length - 2));
  };

  useEffect(() => {
    async function fetch() {
      try {
        setStateWhenMounted({ isLoading: true });
        if (!isAutoComplete) {
          let data = memoryData.length ? memoryData : await getByUrl(fullUrl);
          if (lookup === LookupType.CLASS_LEVELS) {
            data = getSortedClassLevels(data);
          }
          setStateWhenMounted({ data });
        } else if (value && !isAutoCompleMounted.current) {
          isAutoCompleMounted.current = true;
          const data = await getByUrl(fullUrl, { id: value as string });
          setStateWhenMounted({ data });
        }
      } catch (err) {
        // no-op
      } finally {
        setStateWhenMounted({ isLoading: false });
      }
    }

    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoComplete, value, fullUrl, setStateWhenMounted]);

  const createOptionsFromSourceData = useCallback(() => {
    const { data } = state;
    return {
      options: data.map((item) => ({
        label: transformTitle(item[titleName], item),
        value: item[valueName],
      })),
    };
  }, [state, titleName, valueName, transformTitle]);

  const getGeneratedProps = useCallback(() => {
    let sourceData = state.data || INITIAL_DATA;

    if (!Array.isArray(sourceData) && dataSource.sourceField) {
      sourceData = get(sourceData, dataSource.sourceField);
    }

    if (Array.isArray(sourceData)) {
      if (hasOptions) {
        return createOptionsFromSourceData();
      } else if (isMultiSelectPhotos) {
        const personas: IPersona[] = sourceData.map(convertMasterTableDataToPersona);
        return {
          personas,
        };
      } else {
        console.error(`Not implemented for form component type: ${type}`);
      }
    } else {
      console.error('dataSource currently only supports array of data');
    }
    return {};
  }, [state.data, dataSource.sourceField, hasOptions, isMultiSelectPhotos, type, createOptionsFromSourceData]);

  const handleSearch = useCallback(
    debounce(async (value: string) => {
      if (!value || value.length < MIN_SEARCH_CHARACTERS) {
        return;
      }
      try {
        setStateWhenMounted({ isLoading: true });
        const data = await getByUrl(fullUrl, { search: value });
        setStateWhenMounted({ data });
      } catch (err) {
        // no-op
      } finally {
        setStateWhenMounted({ isLoading: false });
      }
    }, DEBOUNCE_DELAY_IN_MS),
    [isAutoComplete, value, fullUrl, setStateWhenMounted]
  );

  const childProps = {
    value,
    ...rest,
    ...getGeneratedProps(),
  };

  if ([FormComponentType.Dropdown, FormComponentType.AutoComplete].includes(type)) {
    (childProps as SelectProps).loading = state.isLoading;
  }
  if (isAutoComplete) {
    (childProps as AutoCompleteProps).onSearch = handleSearch;
  }

  return <Fragment>{children && cloneElement(children, childProps)}</Fragment>;
});

export default WithDataSource;
